import { useMemo } from 'react';

import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

interface InviteCooldownModalProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  remainingMs: number;
}

const formatRemaining = (remainingMs: number) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return { minutes, seconds };
};

/**
 * Informational modal shown when the user tries to (re)send an invite
 * before the Cognito-imposed 1-hour cooldown has elapsed.
 */
export function InviteCooldownModal({
  open,
  onOpen,
  onClose,
  remainingMs,
}: InviteCooldownModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);

  const { minutes, seconds } = useMemo(() => formatRemaining(remainingMs), [remainingMs]);

  const waitMessage =
    minutes > 0
      ? t('authenticated.team:inviteCooldown.waitMinutes', { minutes })
      : t('authenticated.team:inviteCooldown.waitSeconds', { seconds });

  const messageContent = (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-foreground">
        <Clock className="h-4 w-4" />
        <span className="font-semibold">{waitMessage}</span>
      </div>
      <p>{t('authenticated.team:inviteCooldown.reason')}</p>
    </div>
  );

  return (
    <ConfirmationModal
      title={t('authenticated.team:inviteCooldown.title')}
      message={messageContent}
      confirmText={t('common:close')}
      confirmVariant="default"
      hideCancelButton
      onConfirm={closeModal => closeModal()}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    />
  );
}
